import { useState, memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDashboardTranslation } from '@/i18n/hooks/useTypedTranslation';
import type { Survey } from '@/types';
import { formatDate } from '@/utils/dateUtils';

interface SurveyCardProps {
  survey: Survey;
  onDelete: (id: number) => void;
  onShare: (survey: Survey) => void;
  onDuplicate: (survey: Survey) => void;
}

export const SurveyCard = memo(function SurveyCard({ survey, onDelete, onShare, onDuplicate }: SurveyCardProps) {
  const { t } = useDashboardTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDelete = useCallback(() => {
    handleMenuClose();
    onDelete(survey.id);
  }, [handleMenuClose, onDelete, survey.id]);

  const handleShare = useCallback(() => {
    handleMenuClose();
    onShare(survey);
  }, [handleMenuClose, onShare, survey]);

  const handleDuplicate = useCallback(() => {
    handleMenuClose();
    onDuplicate(survey);
  }, [handleMenuClose, onDuplicate, survey]);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
        '&:hover .menu-button': {
          opacity: 1,
        },
      }}
    >
      {/* Status Indicator Bar */}
      <Box
        sx={{
          height: 4,
          width: '100%',
          bgcolor: survey.isActive ? '#2BC4AD' : '#9CA3AF',
          opacity: survey.isActive ? 1 : 0.3,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {survey.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {survey.description || t('card.noDescription')}
            </Typography>
          </Box>
          <IconButton
            className="menu-button"
            onClick={handleMenuOpen}
            size="small"
            sx={{
              opacity: { xs: 1, md: 0 },
              transition: 'opacity 0.2s',
            }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Badges */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            size="small"
            label={survey.isActive ? t('card.active') : t('card.inactive')}
            sx={{
              bgcolor: survey.isActive ? alpha('#2BC4AD', 0.15) : alpha('#9CA3AF', 0.1),
              color: survey.isActive ? '#16A085' : '#4B5563',
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 1.5,
              height: 24,
              '& .MuiChip-icon': {
                color: 'inherit',
                ml: 0.5,
              },
            }}
            icon={
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: survey.isActive ? '#2BC4AD' : '#9CA3AF',
                  ml: 1,
                }}
              />
            }
          />
          <Chip
            size="small"
            variant="outlined"
            icon={survey.isPublic ? <PublicIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
            label={survey.isPublic ? t('card.public') : t('card.private')}
            sx={{ fontWeight: 500 }}
          />
          {survey.requiresInvitation && (
            <Chip
              size="small"
              variant="outlined"
              icon={<PeopleIcon sx={{ fontSize: 14 }} />}
              label={t('card.inviteOnly')}
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatDate(survey.createdDate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {t('card.responses', { count: survey.responseCount ?? 0 })}
            </Typography>
          </Box>
        </Box>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            component={Link}
            to={`/surveys/${survey.id}/edit`}
            variant="outlined"
            size="small"
            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            sx={{ flex: 1 }}
          >
            {t('card.edit')}
          </Button>
          <Button
            component={Link}
            to={`/surveys/${survey.id}/responses`}
            variant="outlined"
            size="small"
            startIcon={<BarChartIcon sx={{ fontSize: 16 }} />}
            sx={{ flex: 1 }}
          >
            {t('card.analytics')}
          </Button>
          {/* For public surveys: navigate to the survey. For private surveys: open share modal */}
          {survey.isPublic ? (
            <Tooltip title={t('card.actions.preview')} arrow>
              <Button
                component={Link}
                to={`/s/${survey.publicToken || survey.id}`}
                variant="contained"
                size="small"
                sx={{ minWidth: 40, px: 1 }}
              >
                <ArrowForwardIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title={t('card.actions.share')} arrow>
              <Button
                variant="contained"
                size="small"
                onClick={handleShare}
                sx={{ minWidth: 40, px: 1 }}
              >
                <ShareIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          )}
        </Box>
      </CardContent>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        disableScrollLock
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem
          component={Link}
          to={`/surveys/${survey.id}/edit`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('card.menu.edit')}</ListItemText>
        </MenuItem>
        <MenuItem
          component={Link}
          to={`/surveys/${survey.id}/responses`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <BarChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('card.menu.responses')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('card.menu.share')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('card.menu.duplicate')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleDelete}
          sx={{
            color: 'error.main',
            '&:hover': {
              bgcolor: alpha('#ef4444', 0.1),
            },
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('card.menu.delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
});

export default SurveyCard;
